/**
 * Customer-detail stat strip — 4 tiles below the cover (Outstanding /
 * Total YTD / Avg payment time / Credit usage), per `prototype/sales/customer.html
 * ▸ .stat-strip`.
 *
 * Values are supplied by the detail route from `invoices.list(customerId)`.
 * Receipt-derived metrics render as unavailable until receipts expose them.
 */
import type { ReactNode } from "react";
import { Clock, DollarSign, TrendingUp, type LucideIcon } from "lucide-react";
import { cn } from "@flint/ui";

type Tone = "indigo" | "amber" | "rose";

export interface DetailStatStripMetrics {
  outstandingValue: string;
  outstandingUnit: string;
  outstandingMeta: ReactNode;
  outstandingTone: Tone;
  ytdValue: string;
  ytdUnit: string;
  ytdMeta: ReactNode;
  avgPaymentValue: string;
  avgPaymentUnit: string;
  avgPaymentMeta: ReactNode;
  creditValue: string;
  creditUnit: string;
  creditMeta: ReactNode;
  creditTone: Tone;
}

const TONE_STYLES: Record<Tone, { tile: string; stripe: string; iconBg: string; iconFg: string }> =
  {
    indigo: { tile: "s-indigo", stripe: "bg-sky-600", iconBg: "bg-sky-50", iconFg: "text-sky-700" },
    amber: { tile: "s-amber", stripe: "bg-warn", iconBg: "bg-warn-50", iconFg: "text-warn" },
    rose: { tile: "s-rose", stripe: "bg-bad", iconBg: "bg-bad-50", iconFg: "text-bad" },
  };

export function DetailStatStrip({ metrics }: { metrics: DetailStatStripMetrics }) {
  return (
    <section className="customer-detail-stat-strip">
      <Tile
        label="Outstanding"
        value={metrics.outstandingValue}
        unit={metrics.outstandingUnit}
        meta={metrics.outstandingMeta}
        tone={metrics.outstandingTone}
        icon={Clock}
      />
      <Tile
        label="Total business · YTD"
        value={metrics.ytdValue}
        unit={metrics.ytdUnit}
        meta={metrics.ytdMeta}
        tone="indigo"
        icon={TrendingUp}
      />
      <Tile
        label="Avg payment time"
        value={metrics.avgPaymentValue}
        unit={metrics.avgPaymentUnit}
        meta={metrics.avgPaymentMeta}
        tone="amber"
        icon={Clock}
      />
      <Tile
        label="Credit usage"
        value={metrics.creditValue}
        unit={metrics.creditUnit}
        meta={metrics.creditMeta}
        tone={metrics.creditTone}
        icon={DollarSign}
      />
    </section>
  );
}

function Tile({
  label,
  value,
  unit,
  meta,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  unit: string;
  meta: ReactNode;
  tone: Tone;
  icon: LucideIcon;
}) {
  const styles = TONE_STYLES[tone];
  return (
    <div className={cn("customer-detail-stat-tile", styles.tile)}>
      <span aria-hidden className={cn("customer-detail-stat-stripe", styles.stripe)} />
      <div className="customer-detail-stat-head">
        <span>{label}</span>
        <span aria-hidden className={cn("customer-detail-stat-ic", styles.iconBg, styles.iconFg)}>
          <Icon size={14} strokeWidth={2} />
        </span>
      </div>
      <div className="customer-detail-stat-val">
        <span className="num">{value}</span>
        <span className="u">{unit}</span>
      </div>
      <div className="customer-detail-stat-meta">{meta}</div>
    </div>
  );
}
